import React from 'react';
import {
  Grid,
  Typography,
  Box,
  BottomNavigation,
  BottomNavigationAction,
  IconButton,
  InputLabel,
  FormControl,
  Select,
  MenuItem,
} from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';
import { useTranslation } from 'react-i18next';
import GestureIcon from '@material-ui/icons/Gesture';
import BrushIcon from '@material-ui/icons/Brush';
import DeleteIcon from '@material-ui/icons/Delete';
import Button from '../../../../../components/Button';
import { PAINT_ITEMS_CONFIG, getAvailableBrandOptions } from './brandColorOptions';

const useStyles = makeStyles(() => ({
  buttonContainer: {
    display: 'flex',
    flexDirection: 'column',
    marginBottom: '16px',
    overflowX: 'auto',
    overflowY: 'hidden',
    width: '100%',
  },
  title: {
    fontSize: '16px',
    fontWeight: 'bold',
    marginBottom: '10px',
  },
  drawingTypeContainer: {
    marginTop: '10px',
    marginBottom: '10px',
  },
  lengthValue: {
    fontWeight: 'normal',
  },
  iconWrapper: {
    borderRadius: '8px',
    padding: '14px 14px 15px 14px',
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    transition: 'transform 0.1s ease',
    '&:active': {
      transform: 'scale(0.9)',
    },
  },
}));

const ColorButtons = ({
  selectedColor,
  onColorSelect,
  onClearLinesByColor,
  onClearAllVectors,
  onCopyVectorsByColor,
  availableCopyColors = {},
  drawingType = 'line',
  onDrawingTypeChange,
  lines = [],
  teleauditStatus,
  onBrandOptionChange,
  initialColorOptions,
  brandOptions = [],
  // productName = 'productName',
}) => {
  const { t } = useTranslation();
  const styles = useStyles();

  // Sin marcas por defecto: la asignación color -> marca siempre viene del scope.
  const colorOptions = initialColorOptions || PAINT_ITEMS_CONFIG.reduce(
    (options, item) => ({ ...options, [item.key]: 0 }),
    {},
  );

  const calculateLineLengths = () => {
    const lengths = {};

    if (lines && lines.length > 0) {
      lines.forEach((line) => {
        if (line.color) {
          const length = Math.hypot(line.x2 - line.x1, line.y2 - line.y1);

          if (!lengths[line.color]) {
            lengths[line.color] = 0;
          }
          lengths[line.color] += length;
        }
      });
    }

    return lengths;
  };

  const lineLengths = calculateLineLengths();

  // Calcular porcentajes SOS para cada marca
  const totalLength = Object.values(lineLengths).reduce(
    (acc, val) => acc + val,
    0,
  );

  const sosPercentages = {};
  if (totalLength > 0) {
    Object.keys(lineLengths).forEach((colorName) => {
      sosPercentages[colorName] = (lineLengths[colorName] / totalLength) * 100;
    });
  }

  function PaintItem({
    colorName,
    colorValue,
    onColorSelect,
    onClearLinesByColor,
    onCopyVectorsByColor,
    option,
    setOption,
    teleauditStatus,
  }) {
    const value = Math.round(lineLengths[colorName] || 0);
    const availableCopyColor = availableCopyColors[colorName];

    return (
      <Grid container item xs={12} direction="column" style={{ minWidth: 500 }}>
        <Box
          display="grid"
          gridTemplateColumns="minmax(190px, 1fr) minmax(70px, 1fr) minmax(50px, 1fr) minmax(50px, 1fr) minmax(100px, 1fr)"
          alignItems="center"
          mb={2}
          style={{ width: '100%', minWidth: 500, margin: '0 auto' }}
        >
          {/* Select */}
          <FormControl
            variant="outlined"
            size="small"
            style={{ width: '100%', minWidth: 0 }}
          >
            <InputLabel>Opciones</InputLabel>
            <Select
              value={option}
              onChange={(e) => setOption(Number(e.target.value))}
              label="Opciones"
              disabled={teleauditStatus === 'done'}
            >
              {getAvailableBrandOptions(
                brandOptions,
                colorOptions,
                colorName,
                option,
              ).map((opt) => (
                <MenuItem key={opt.value} value={opt.value}>
                  {opt.label}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          {/* Button */}
          <BottomNavigation
            showLabels
            style={{ width: '100%', minWidth: 0 }}
          >
            <BottomNavigationAction
              disableRipple
              disabled={!!((teleauditStatus === 'done' || option === 0))}
              onClick={() => onColorSelect(colorName)}
              style={{ width: '100%', minWidth: 0 }}
              icon={
                <Box
                  className={styles.iconWrapper}
                  style={{
                    backgroundColor: option === 0 ? '#dfdfdfff' : '#fff',
                    border:
                      selectedColor !== colorName
                        ? '1px solid #ddd'
                        : `2px solid ${colorValue}`,
                  }}
                >
                  <GestureIcon style={{ color: colorValue }} />
                </Box>
              }
            />
          </BottomNavigation>

          {/* Single Progress Bar for selected option */}
          <Box style={{ width: '100%', minWidth: 0, textAlign: 'center' }}>
            <Typography
              variant="h4"
              component="div"
              style={{ color: colorValue, fontWeight: 'bold' }}
            >
              {value}
            </Typography>
          </Box>
          <Box style={{ width: '100%', minWidth: 0 }}>
            <Typography
              variant="h4"
              component="div"
              color="textSecondary"
              style={{ textAlign: 'center' }}
            >
              {sosPercentages[colorName]
                ? `${sosPercentages[colorName].toFixed(0)}%`
                : '0%'}
            </Typography>
          </Box>
          <Box
            style={{
              width: '100%',
              minWidth: 0,
              display: 'flex',
            }}
          >
            <IconButton
              aria-label={t('teleaudit.copyStrokes')}
              disabled={teleauditStatus === 'done' || value === 0 || !availableCopyColor}
              onClick={() => onCopyVectorsByColor(colorName)}
              title={t('teleaudit.copyStrokesTooltip')}
              style={{
                width: 50,
                minWidth: 50,
                height: 56,
                padding: 8,
                color: teleauditStatus === 'done' || !availableCopyColor
                  ? '#9e9e9e'
                  : '#2C9DC9',
                flex: '1 1 50px',
                flexShrink: 0,
              }}
            >
              <span style={{ fontSize: 20 }}>⧉</span>
            </IconButton>
            <IconButton
              aria-label={t('teleaudit.deleteLines')}
              disabled={teleauditStatus === 'done'}
              onClick={() => onClearLinesByColor(colorName)}
              title={t('teleaudit.deleteLines')}
              style={{
                width: 50,
                minWidth: 50,
                height: 56,
                padding: 8,
                color: teleauditStatus === 'done' ? '#9e9e9e' : '#d32f2f',
                flex: '1 1 50px',
                flexShrink: 0,
              }}
            >
              <DeleteIcon />
            </IconButton>
          </Box>
          {/* <TextField
            name="target"
            type="text"
            size="medium"
            placeholder="Target"
            variant="outlined"
            style={{ width: 150 }}
          /> */}
        </Box>
      </Grid>
    );
  }

  return (
    <Box className={styles.buttonContainer}>
      <Box display="flex" justifyContent="space-between" alignItems="center">
        <Box mt={4} mb={4} display="flex" direction="row" alignItems="center">
          <BrushIcon color="primary" />
          <Typography variant="h2">Dibujar trazos</Typography>
        </Box>
        <Button
          id="clear-all-vectors-btn"
          text={t('teleaudit.deleteStrokes')}
          variant="contained"
          height="36px"
          width="170px"
          backgroundColor={teleauditStatus === 'done' ? '#D3D3D3' : '#2C9DC9'}
          color="white"
          borderRadius="10px"
          disabled={teleauditStatus === 'done'}
          onClick={onClearAllVectors}
        />
      </Box>

      <Grid container spacing={2} style={{ width: '100%', margin: 0 }}>
        <Grid item xs={12}>
          <Box
            display="grid"
            gridTemplateColumns="minmax(190px, 1fr) minmax(70px, 1fr) minmax(50px, 1fr) minmax(50px, 1fr) minmax(100px, 1fr)"
            alignItems="center"
            style={{ width: '100%', minWidth: 500, margin: '0 auto' }}
          >
            <Typography
              variant="h4"
              style={{
                width: '100%',
                minWidth: 0,
                flex: 'none',
                display: 'flex',
                justifyContent: 'center',
              }}
            >
              Marca
            </Typography>
            <Typography
              variant="h4"
              style={{
                width: '100%',
                minWidth: 0,
                flex: 'none',
                display: 'flex',
                justifyContent: 'center',
              }}
            >
              Color
            </Typography>
            <Typography
              variant="h4"
              style={{
                width: '100%',
                minWidth: 0,
                flex: 'none',
                textAlign: 'center',
                whiteSpace: 'normal',
                lineHeight: 1.2,
              }}
            >
              Longitud (px)
            </Typography>
            <Typography
              variant="h4"
              style={{
                width: '100%',
                minWidth: 0,
                flex: 'none',
                display: 'flex',
                justifyContent: 'center',
              }}
            >
              %
            </Typography>
            <Typography
              variant="h4"
              style={{
                width: '100%',
                minWidth: 0,
                flex: 'none',
                display: 'flex',
                justifyContent: 'center',
              }}
            >
              {t('teleaudit.actions')}
            </Typography>
            {/* <Typography
                variant="h4"
                style={{ width: 130, minWidth: 130, flex: '1 1 130px', textAlign: 'center' }}
              >
                Target
              </Typography> */}
          </Box>
        </Grid>

        {PAINT_ITEMS_CONFIG
          .slice(0, brandOptions.filter((option) => option.value !== 0).length)
          .map((item) => (
            <PaintItem
              key={item.key}
              colorName={item.key}
              colorValue={item.color}
              onColorSelect={onColorSelect}
              onClearLinesByColor={onClearLinesByColor}
              onCopyVectorsByColor={onCopyVectorsByColor}
              option={colorOptions[item.key]}
              teleauditStatus={teleauditStatus}
              setOption={(newValue) => {
                if (onBrandOptionChange) {
                  onBrandOptionChange((prev) => ({
                    ...prev,
                    [item.key]: newValue,
                  }));
                }
              }}
            />
          ))}
      </Grid>

      <Box mt={2} mb={2} style={{ display: 'none' }}>
        <Typography className={styles.title}>Tipo de trazo</Typography>
        <Grid container spacing={2} justifyContent="center">
          <Grid item>
            <button
              onClick={() => onDrawingTypeChange('line')}
              style={{
                backgroundColor: drawingType === 'line' ? '#2C9DC9' : '#f5f5f5',
                color: drawingType === 'line' ? 'white' : 'black',
                border: '2px solid #2C9DC9',
                borderRadius: '4px',
                padding: '6px 12px',
                cursor: 'pointer',
                fontWeight: 'bold',
                minWidth: '100px',
                boxShadow:
                  drawingType === 'line' ? '0 0 5px rgba(0,0,0,0.3)' : 'none',
              }}
            >
              Líneas
            </button>
          </Grid>
          <Grid item>
            <button
              onClick={() => onDrawingTypeChange('rect')}
              style={{
                backgroundColor: drawingType === 'rect' ? '#2C9DC9' : '#f5f5f5',
                color: drawingType === 'rect' ? 'white' : 'black',
                border: '2px solid #2C9DC9',
                borderRadius: '4px',
                padding: '6px 12px',
                cursor: 'pointer',
                fontWeight: 'bold',
                minWidth: '100px',
                boxShadow:
                  drawingType === 'rect' ? '0 0 5px rgba(0,0,0,0.3)' : 'none',
              }}
            >
              Rectángulos
            </button>
          </Grid>
        </Grid>
      </Box>

    </Box>
  );
};

export default ColorButtons;
